import { useState } from 'react';
import {
  WEEKDAYS,
  WEEKDAY_LABELS,
  addWeeks,
  weekRangeLabel,
  type Weekday,
  type WeekKey,
} from '@td/shared';
import { useCopyPlan } from '../hooks/useLessonPlans';
import { ApiRequestError } from '../lib/api';
import { Modal } from './Modal';

export type CopyScope =
  | { kind: 'cell'; rowId: string; weekday: Weekday; label: string }
  | { kind: 'row'; rowId: string; label: string }
  | { kind: 'day'; weekday: Weekday; label: string };

/**
 * Choose where a cell, subject or day should be copied to.
 *
 * Weeks are offered as offsets from the one being viewed rather than a date
 * picker — copying is nearly always "next week", occasionally "the week
 * after", and almost never an arbitrary date in March.
 */
export function CopyPlanMenu({
  week,
  scope,
  onClose,
}: {
  week: WeekKey;
  scope: CopyScope;
  onClose: () => void;
}) {
  const copy = useCopyPlan();
  const [weekOffset, setWeekOffset] = useState(scope.kind === 'row' ? 1 : 0);
  const [days, setDays] = useState<Weekday[]>(
    scope.kind === 'cell' ? [] : scope.kind === 'day' ? [] : [],
  );
  const [overwrite, setOverwrite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const targetWeek = addWeeks(week, weekOffset);
  const picksDays = scope.kind === 'cell' || scope.kind === 'day';
  const canSubmit = !picksDays || days.length > 0;

  function toggleDay(d: Weekday) {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  async function submit() {
    setError(null);
    try {
      const result = await copy.mutateAsync({
        from: {
          week,
          rowId: scope.kind === 'day' ? undefined : scope.rowId,
          weekday: scope.kind === 'row' ? undefined : scope.weekday,
        },
        to: {
          week: targetWeek,
          weekdays: picksDays ? days : undefined,
        },
        overwrite,
      });
      setDone(
        `Copied ${result.copied} cell${result.copied === 1 ? '' : 's'}` +
          (result.skipped > 0 ? `, left ${result.skipped} alone` : '') +
          '.',
      );
      setTimeout(onClose, 1200);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not copy that.');
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Copy"
      className="w-full max-w-sm rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-1 text-lg">Copy</h2>
      <p className="mb-4 text-xs text-stone">{scope.label}</p>

      <div className="mb-4">
        <span className="label">To which week</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekOffset((o) => o - 1)}
            className="btn-secondary px-2.5 py-1.5"
            aria-label="Earlier week"
          >
            ←
          </button>
          <div className="min-w-[150px] px-1 text-center">
            <div className="text-sm text-espresso">{weekRangeLabel(targetWeek)}</div>
            <div className="text-[11px] text-stone">
              {weekOffset === 0
                ? 'This week'
                : weekOffset === 1
                  ? 'Next week'
                  : weekOffset === -1
                    ? 'Previous week'
                    : weekOffset > 0
                      ? `${weekOffset} weeks ahead`
                      : `${Math.abs(weekOffset)} weeks back`}
            </div>
          </div>
          <button
            onClick={() => setWeekOffset((o) => o + 1)}
            className="btn-secondary px-2.5 py-1.5"
            aria-label="Later week"
          >
            →
          </button>
        </div>
      </div>

      {picksDays && (
        <fieldset className="mb-4">
          <legend className="label">Onto which days</legend>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((d) => {
              // Only reachable when scope has a weekday, since `picksDays`
              // gates this whole block to the cell and day scopes.
              const sourceDay = 'weekday' in scope ? scope.weekday : null;
              const isSource = weekOffset === 0 && d === sourceDay;
              return (
                <button
                  key={d}
                  onClick={() => toggleDay(d)}
                  disabled={isSource}
                  title={isSource ? 'This is where it is being copied from' : undefined}
                  className={[
                    'rounded-lg border px-2.5 py-1 text-xs transition-colors',
                    days.includes(d)
                      ? 'border-terracotta bg-terracotta text-cream'
                      : 'border-sand text-walnut hover:bg-linen',
                    isSource ? 'cursor-not-allowed opacity-30' : '',
                  ].join(' ')}
                >
                  {WEEKDAY_LABELS[d].slice(0, 3)}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      <label className="mb-4 flex items-center gap-2 text-sm text-walnut">
        <input
          type="checkbox"
          checked={!overwrite}
          onChange={(e) => setOverwrite(!e.target.checked)}
          className="h-4 w-4 accent-terracotta"
        />
        Keep anything already written
      </label>

      {error && (
        <p role="alert" className="mb-3 rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
          {error}
        </p>
      )}
      {done && <p className="mb-3 text-sm text-olive">{done}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button
          onClick={() => void submit()}
          className="btn-primary"
          disabled={!canSubmit || copy.isPending}
        >
          {copy.isPending ? 'Copying…' : 'Copy'}
        </button>
      </div>
    </Modal>
  );
}
